import { DateRange, FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsReportResultsQuery } from 'generated/resources/graphql'

type Schema =
  AnalyticsReportResultsQuery['iris']['sqlResultsConnection']['nodes'][number]['schema'][number]

function filterForType(column: Schema): FiltersDefinition<string> {
  const label = column.displayName ?? column.name
  if (column.enumValues.length > 0) {
    return {
      [column.name]: {
        type: 'in',
        label,
        options: {
          options: column.enumValues.map((value) => ({ value, label: value })),
        },
      },
    }
  }

  if (column.dataType === 'string' && column.formatHint === 'date') {
    return { [column.name]: { type: 'date', options: { mode: 'range' }, label } }
  }

  if (column.dataType === 'string') {
    return { [column.name]: { type: 'search', label } }
  }

  if (column.dataType === 'number') {
    return { [column.name]: { type: 'number', label } }
  }

  return {}
}

export function filterDefinitionsFromSchema(schema: Schema[]): FiltersDefinition<string> {
  return schema.map(filterForType).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

type JsonLogicOperator =
  | 'var'
  | 'missing'
  | 'missing_some'
  | 'if'
  | '=='
  | '==='
  | '!='
  | '!=='
  | '!'
  | '!!'
  | 'or'
  | 'and'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'between'
  | 'max'
  | 'min'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | 'map'
  | 'filter'
  | 'reduce'
  | 'all'
  | 'none'
  | 'some'
  | 'merge'
  | 'in'
  | 'cat'
  | 'substr'
  | 'log'

type JsonLogicFilter = {
  [K in JsonLogicOperator]?: JsonLogicFilter | unknown | Array<JsonLogicFilter | unknown>
}

type VarExpression = {
  var: string
}

type InExpressionEnum = {
  in: [VarExpression, Array<unknown>]
}

type InExpressionSearch = {
  in: [string, VarExpression]
}

type GreaterThanOrEqualExpression = {
  '>=': [VarExpression, number]
}

type LessThanOrEqualExpression = {
  '<=': [VarExpression, number]
}

type BetweenExpression = {
  between: [string | number | Date, VarExpression, string | number | Date]
}

type Expression =
  | InExpressionEnum
  | InExpressionSearch
  | BetweenExpression
  | GreaterThanOrEqualExpression
  | LessThanOrEqualExpression

type AndExpression = {
  and: Array<Expression>
}

type FilterResult = {
  fieldName: string
  value: unknown
}

type ParseErrorType = 'INVALID_STRUCTURE'

type ParseError = {
  type: ParseErrorType
  message: string
  context?: Record<string, unknown>
}

type ParseResult = { success: true; filter: FilterResult } | { success: false; error: ParseError }

export function datatableFiltersToJsonLogicFilters(
  filters: FiltersState<FiltersDefinition<string>>
): JsonLogicFilter | undefined {
  const conditions = Object.entries(filters)
    .map(([fieldName, filterValue]) => {
      // Skip undefined/null/empty values
      if (filterValue === undefined || filterValue === null || filterValue === '') {
        return null
      }

      // Number filters
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const startValue = filterValue[0]
        const endValue = filterValue[1]
        if (startValue === undefined || endValue === undefined) {
          if (endValue === undefined)
            return {
              '>=': [{ var: fieldName }, startValue],
            }
          if (startValue === undefined)
            return {
              '<=': [{ var: fieldName }, endValue],
            }
        }
        return { between: [startValue, { var: fieldName }, endValue] }
      }

      // Skip enum filters (arrays) - not implemented
      if (Array.isArray(filterValue)) {
        return null
      }

      // Check for DateRange object: { from: Date, to: Date }
      if (isDateRange(filterValue)) {
        return { between: [filterValue.from, { var: fieldName }, filterValue.to] }
      }

      // Everything else: treat as string search
      return { in: [String(filterValue), { var: fieldName }] }
    })
    .filter((condition): condition is NonNullable<typeof condition> => condition !== null)

  if (conditions.length === 0) {
    return undefined
  }

  return { and: conditions }
}

// ============================================================================
// Type Guards
// ============================================================================

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isVarExpression = (value: unknown): value is VarExpression => {
  return isObject(value) && 'var' in value && typeof value.var === 'string'
}

const isGreaterThanOrEqualExpression = (value: unknown): value is GreaterThanOrEqualExpression => {
  return isObject(value) && '>=' in value
}

const isLessThanOrEqualExpression = (value: unknown): value is LessThanOrEqualExpression => {
  return isObject(value) && '<=' in value
}

const isInExpressionEnum = (value: unknown): value is InExpressionEnum => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in
  return value.in.length === 2 && isVarExpression(first) && Array.isArray(second)
}

const isInExpressionSearch = (value: unknown): value is InExpressionSearch => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr = Array.isArray(second) && second.length === 1 ? second[0] : second

  return value.in.length === 2 && typeof first === 'string' && isVarExpression(varExpr)
}

const isBetweenExpression = (value: unknown): value is BetweenExpression => {
  return (
    isObject(value) &&
    'between' in value &&
    Array.isArray(value.between) &&
    value.between.length === 3 &&
    (typeof value.between[0] === 'string' ||
      typeof value.between[0] === 'number' ||
      value.between[0] instanceof Date) &&
    (typeof value.between[2] === 'string' ||
      typeof value.between[2] === 'number' ||
      value.between[2] instanceof Date) &&
    isVarExpression(value.between[1])
  )
}

const isSuccessResult = (
  result: ParseResult
): result is { success: true; filter: FilterResult } => {
  return result.success
}

const isDateRange = (value: unknown): value is DateRange => {
  return (
    isObject(value) &&
    'from' in value &&
    'to' in value &&
    value.from instanceof Date &&
    value.to instanceof Date
  )
}

const extractAndConditions = (
  parsed: unknown
): { success: true; data: unknown[] } | { success: false; error: string } => {
  if (!isObject(parsed) || !('and' in parsed) || !Array.isArray(parsed.and)) {
    return { success: false, error: 'Missing "and" array in JsonLogic structure' }
  }
  return { success: true, data: parsed.and }
}

// ============================================================================
// Pattern Matchers
// ============================================================================

const parseInExpressionEnum = (expression: InExpressionEnum): ParseResult => {
  const fieldName = expression.in[0].var
  const values = expression.in[1]

  const filteredValues = values.filter((v): v is string | number => v !== '')
  if (filteredValues.length === 0) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'No valid values provided' },
    }
  }

  return {
    success: true,
    filter: { fieldName, value: filteredValues },
  }
}

const parseInExpressionSearch = (expression: InExpressionSearch): ParseResult => {
  const searchValue = expression.in[0]
  const secondElement = expression.in[1]

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr =
    Array.isArray(secondElement) && secondElement.length === 1 ? secondElement[0] : secondElement
  const fieldName = varExpr.var

  return {
    success: true,
    filter: { fieldName, value: searchValue },
  }
}

const parseBetweenExpression = (expression: BetweenExpression): ParseResult => {
  const [from, varExpr, to] = expression.between
  const fieldName = varExpr.var

  if (typeof from === 'number' || typeof to === 'number') {
    return {
      success: true,
      filter: {
        fieldName,
        value: [from, to],
      },
    }
  }

  try {
    return {
      success: true,
      filter: {
        fieldName,
        value: {
          from: new Date(from),
          to: new Date(to),
        },
      },
    }
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: `Error parsing dates for column ${fieldName}`,
        context: { fieldName, from, to, error: String(error) },
      },
    }
  }
}

const parseGreaterThanOrEqualExpression = (
  expression: GreaterThanOrEqualExpression
): ParseResult => {
  const [varExpr, value] = expression['>=']
  const fieldName = varExpr.var
  return {
    success: true,
    filter: { fieldName, value: [value, undefined] },
  }
}

const parseLessThanOrEqualExpression = (expression: LessThanOrEqualExpression): ParseResult => {
  const [varExpr, value] = expression['<=']
  const fieldName = varExpr.var
  return {
    success: true,
    filter: { fieldName, value: [undefined, value] },
  }
}

const parseCondition = (condition: unknown): ParseResult => {
  if (!isObject(condition)) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Condition is not an object' },
    }
  }

  // Try parsing as InExpression (enum pattern)
  if (isInExpressionEnum(condition)) {
    return parseInExpressionEnum(condition)
  }

  // Try parsing as InExpression (search pattern)
  if (isInExpressionSearch(condition)) {
    return parseInExpressionSearch(condition)
  }

  if (isGreaterThanOrEqualExpression(condition)) {
    return parseGreaterThanOrEqualExpression(condition)
  }

  if (isLessThanOrEqualExpression(condition)) {
    return parseLessThanOrEqualExpression(condition)
  }

  // Try parsing as BetweenExpression
  if (isBetweenExpression(condition)) {
    return parseBetweenExpression(condition)
  }

  return {
    success: false,
    error: {
      type: 'INVALID_STRUCTURE',
      message: 'Unknown condition pattern',
      context: { condition },
    },
  }
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Converts saved jsonLogic filters back to OneDataCollection FiltersState format.
 * This is the reverse of datatableFiltersToJsonLogicFilters.
 *
 * @param jsonLogicFilters - JSON string containing jsonLogic filter structure
 * @returns FiltersState object that can be used by OneDataCollection
 */
export function jsonLogicFiltersToFiltersState(
  jsonLogicFilters: string
): FiltersState<FiltersDefinition<string>> {
  if (Object.keys(jsonLogicFilters).length === 0) {
    return {}
  }

  const conditionsResult = extractAndConditions(JSON.parse(jsonLogicFilters))
  if (!conditionsResult.success) {
    throw new Error(`Unexpected jsonLogic structure:': ${conditionsResult.error}`)
  }

  const parseResults = conditionsResult.data
    .map((condition) => parseCondition(condition))
    .filter(isSuccessResult)

  const filtersState: Record<string, string | unknown[] | DateRange | Date | undefined> = {}
  parseResults.forEach((result) => {
    const value = result.filter.value
    if (
      typeof value === 'string' ||
      Array.isArray(value) ||
      value instanceof Date ||
      value === undefined ||
      isDateRange(value)
    ) {
      filtersState[result.filter.fieldName] = value
    }
  })

  return filtersState
}
